import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Type, Download, Link as LinkIcon, Sticker, ChevronLeft, Eye, Edit3, Plus, Trash2, Tag, BadgePercent, Layers } from 'lucide-react';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { StoryTemplate, StorySticker, StickerStyle, StoryContent } from '../../types/media';

interface MediaEditorModalProps {
    template: StoryTemplate;
    onClose: () => void;
    /** Contenuti iniziali (es. generati dall'AI) con cui precompilare l'editor. */
    initial?: StoryContent | null;
}

const STICKER_STYLES: { value: StickerStyle; label: string; icon: React.ReactNode }[] = [
    { value: 'pill', label: 'Pill', icon: <Tag size={16} /> },
    { value: 'badge', label: 'Badge', icon: <Sticker size={16} /> },
    { value: 'promo', label: 'Promo', icon: <BadgePercent size={16} /> },
    { value: 'link', label: 'Link', icon: <LinkIcon size={16} /> },
];

const OVERLAY_OPTS: { value: string; label: string }[] = [
    { value: 'bg-black/10', label: 'Leggero' },
    { value: 'bg-black/30', label: 'Medio' },
    { value: 'bg-black/50', label: 'Scuro' },
    { value: 'bg-gradient-to-t from-black/70 via-black/20 to-black/40', label: 'Sfumato' },
];

const newStickerId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** Resa visiva di un'etichetta in anteprima, per stile. */
const StickerView: React.FC<{ sticker: StorySticker }> = ({ sticker }) => {
    switch (sticker.style) {
        case 'promo':
            return (
                <div className="bg-pink-600 text-white min-w-24 min-h-24 p-3 rounded-full flex items-center justify-center font-black italic text-lg shadow-2xl border-4 border-white text-center leading-tight">
                    {sticker.text}
                </div>
            );
        case 'link':
            return (
                <div className="bg-white rounded-lg px-3 py-1.5 shadow-2xl">
                    <span className="text-blue-600 font-bold uppercase tracking-tight flex items-center gap-2 text-sm">
                        <LinkIcon size={15} /> {sticker.text}
                    </span>
                </div>
            );
        case 'badge':
            return (
                <div className="bg-yellow-300 text-black px-4 py-1.5 rounded-md font-black uppercase text-sm shadow-2xl -rotate-3">
                    {sticker.text}
                </div>
            );
        case 'pill':
        default:
            return (
                <div className="bg-black/50 backdrop-blur-md border border-white/30 text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-2xl">
                    {sticker.text}
                </div>
            );
    }
};

const MediaEditorModal: React.FC<MediaEditorModalProps> = ({ template, onClose, initial = null }) => {
    const [editorText, setEditorText] = useState({
        title: initial?.title ?? template.defaultText.title,
        subtitle: initial?.subtitle ?? template.defaultText.subtitle,
    });
    const [stickers, setStickers] = useState<StorySticker[]>(
        initial?.stickers ?? [{ id: newStickerId(), text: template.defaultText.cta, style: 'link' }],
    );
    const [overlay, setOverlay] = useState(template.overlayColor);
    const [isExporting, setIsExporting] = useState(false);
    const [mobileView, setMobileView] = useState<'editor' | 'preview'>('editor');

    const exportRef = useRef<HTMLDivElement>(null);

    // Forza overflow hidden sul body quando il modal è aperto
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, []);

    const addSticker = (style: StickerStyle) => {
        const defaults: Record<StickerStyle, string> = {
            pill: 'Nuova etichetta',
            badge: 'NOVITÀ',
            promo: 'PROMO',
            link: 'PRENOTA ORA',
        };
        setStickers((prev) => [...prev, { id: newStickerId(), text: defaults[style], style }]);
    };

    const updateSticker = (id: string, text: string) =>
        setStickers((prev) => prev.map((s) => (s.id === id ? { ...s, text } : s)));

    const removeSticker = (id: string) =>
        setStickers((prev) => prev.filter((s) => s.id !== id));

    const handleExport = async () => {
        if (!exportRef.current) return;
        setIsExporting(true);
        try {
            // Esportazione in formato 1080x1920
            const dataUrl = await toPng(exportRef.current, {
                canvasWidth: 1080,
                canvasHeight: 1920,
                pixelRatio: 1,
            });
            saveAs(dataUrl, `story-export-${Date.now()}.png`);
        } catch (err) {
            console.error('Export failed', err);
        } finally {
            setIsExporting(false);
        }
    };

    const modalContent = (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-[#0F1115] flex flex-col lg:flex-row text-white h-[100dvh] w-screen overflow-hidden"
        >
            {/* SEZIONE COMANDI */}
            <div className={`w-full lg:w-[400px] bg-[#14171E] border-r border-white/5 flex flex-col h-full z-20 ${mobileView === 'editor' ? 'flex' : 'hidden lg:flex'}`}>
                <div className="p-6 border-b border-white/5 flex items-center gap-4 shrink-0">
                    <button onClick={onClose} className="p-2 bg-white/5 rounded-full hover:bg-white/10 transition-colors">
                        <ChevronLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold tracking-tight">Crea Storia</h2>
                        <p className="text-xs text-gray-500">{template.title}</p>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8 no-scrollbar">
                    {/* INPUT TESTI */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-gray-500 text-[10px] font-bold uppercase tracking-widest">
                            <Type size={14} /> Testi del Modello
                        </div>
                        <input
                            type="text"
                            value={editorText.title}
                            onChange={(e) => setEditorText({ ...editorText, title: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition-all font-bold"
                            placeholder="Titolo"
                        />
                        <textarea
                            value={editorText.subtitle}
                            onChange={(e) => setEditorText({ ...editorText, subtitle: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition-all h-24 resize-none text-sm"
                            placeholder="Sottotitolo"
                        />
                    </div>

                    {/* SFONDO / OVERLAY */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-gray-500 text-[10px] font-bold uppercase tracking-widest">
                            <Layers size={14} /> Intensità Sfondo
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {OVERLAY_OPTS.map((o) => (
                                <button
                                    key={o.value}
                                    onClick={() => setOverlay(o.value)}
                                    className={`p-2.5 rounded-xl border text-[10px] font-bold transition-all ${overlay === o.value ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ETICHETTE */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-gray-500 text-[10px] font-bold uppercase tracking-widest">
                            <Sticker size={14} /> Etichette
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {STICKER_STYLES.map((s) => (
                                <button
                                    key={s.value}
                                    onClick={() => addSticker(s.value)}
                                    title={`Aggiungi etichetta ${s.label}`}
                                    className="p-3 rounded-xl border border-white/10 bg-white/5 text-gray-400 hover:border-blue-500 hover:text-blue-400 transition-all flex flex-col items-center gap-1.5"
                                >
                                    {s.icon}
                                    <span className="text-[9px] font-bold flex items-center gap-0.5"><Plus size={9} />{s.label}</span>
                                </button>
                            ))}
                        </div>
                        {stickers.length === 0 ? (
                            <p className="text-xs text-gray-600">Nessuna etichetta. Aggiungine una con i pulsanti qui sopra.</p>
                        ) : (
                            <div className="space-y-2">
                                {stickers.map((s) => (
                                    <div key={s.id} className="flex items-center gap-2">
                                        <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 shrink-0">
                                            {STICKER_STYLES.find((o) => o.value === s.style)?.icon}
                                        </span>
                                        <input
                                            type="text"
                                            value={s.text}
                                            onChange={(e) => updateSticker(s.id, e.target.value)}
                                            className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-500 transition-all"
                                            placeholder="Testo etichetta"
                                        />
                                        <button
                                            onClick={() => removeSticker(s.id)}
                                            title="Rimuovi etichetta"
                                            className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors shrink-0"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="text-[10px] text-gray-600">Trascina le etichette nell'anteprima per posizionarle.</p>
                    </div>
                </div>

                <div className="p-6 bg-[#14171E] border-t border-white/5 space-y-3">
                    <button onClick={() => setMobileView('preview')} className="lg:hidden w-full py-3 bg-white/10 rounded-xl font-bold flex items-center justify-center gap-2">
                        <Eye size={18} /> Vedi Anteprima
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isExporting}
                        className="w-full py-4 bg-white text-black rounded-2xl font-black text-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isExporting ? <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <><Download size={20} /> SCARICA</>}
                    </button>
                </div>
            </div>

            {/* AREA DI PREVIEW (MOCKUP) */}
            <div className={`flex-1 relative bg-[#090A0D] flex items-center justify-center p-4 lg:p-12 ${mobileView === 'preview' ? 'flex' : 'hidden lg:flex'}`}>
                <button onClick={() => setMobileView('editor')} className="lg:hidden absolute top-10 left-6 z-50 bg-black/50 p-3 rounded-full border border-white/10 text-white">
                    <Edit3 size={20} />
                </button>

                {/* IPHONE MOCKUP */}
                <div className="relative aspect-[9/16] h-full max-h-[85vh] lg:max-h-[92%] bg-[#121212] rounded-[50px] p-2 shadow-2xl border-[6px] border-[#222]">
                    <div className="absolute top-7 left-1/2 -translate-x-1/2 w-[30%] h-[4%] bg-black rounded-full z-50 pointer-events-none flex items-center justify-end px-3">
                        <div className="w-2 h-2 rounded-full bg-[#1a1a1a] opacity-50"></div>
                    </div>
                    {/* Target area per l'esportazione */}
                    <div ref={exportRef} className="relative w-full h-full bg-black rounded-[40px] overflow-hidden select-none">
                        <img src={template.bgImage} className="w-full h-full object-cover pointer-events-none" alt="" />
                        <div className={`absolute inset-0 ${overlay} pointer-events-none`} />

                        <div className="absolute inset-0 p-10 flex flex-col items-center">
                            <motion.div
                                drag dragMomentum={false}
                                className="w-full text-center cursor-move pt-20"
                            >
                                <h2 className="text-2xl lg:text-3xl font-black text-white uppercase drop-shadow-2xl leading-none">
                                    {editorText.title}
                                </h2>
                                {editorText.subtitle && (
                                    <div className="mt-4 bg-black/40 backdrop-blur-md px-6 py-2 rounded-xl border border-white/20 inline-block">
                                        <p className="text-lg font-medium text-white leading-tight">{editorText.subtitle}</p>
                                    </div>
                                )}
                            </motion.div>

                            <div className="mt-auto mb-32 w-full flex flex-col items-center gap-5">
                                {stickers.map((s) => (
                                    <motion.div key={s.id} drag dragMomentum={false} className="cursor-move">
                                        <StickerView sticker={s} />
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );

    return createPortal(modalContent, document.body);
};

export default MediaEditorModal;
